import React, { useEffect, useState } from "react";
import { Download, Smartphone, X, Check, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, resolveImage } from "@/lib/api";
import OmkarLogo from "@/components/OmkarLogo";

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(true);
  const [isInstalled, setIsInstalled] = useState(false);
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    api.get("/whitelabel")
      .then(res => setBranding(res.data))
      .catch(() => {});
  }, []);

  const logoUrl = branding?.logo_url ? resolveImage(branding.logo_url) : null;
  const companyName = branding?.company_name || "Omkar Dairy";

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowBanner(false);
      setDeferredPrompt(null);
    };

    const handleOpenGuide = () => {
      handleInstallClick();
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("open-pwa-guide", handleOpenGuide);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("open-pwa-guide", handleOpenGuide);
    };
    /* eslint-disable-next-line */
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // If browser hasn't fired beforeinstallprompt or desktop browser without native API, show instructions modal
      setGuideModalOpen(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem("pwa_install_dismissed", "true");
  };

  const dismissed = sessionStorage.getItem("pwa_install_dismissed");

  return (
    <>
      {/* Top Banner (Dismissible) */}
      {!isInstalled && showBanner && !dismissed && (
        <div className="bg-slate-900 text-white px-4 py-2.5 shadow-md border-b border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="w-10 h-10 rounded-xl bg-white p-1 flex items-center justify-center shrink-0 border border-slate-700 shadow-sm overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="w-full h-full object-contain" />
              ) : (
                <OmkarLogo size={32} />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm text-white tracking-wide">Install {companyName} App</span>
                <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-[10px] uppercase font-bold px-2 py-0.5 rounded">PWA</span>
              </div>
              <p className="text-xs text-slate-300 font-normal mt-0.5">
                Install on mobile/desktop for 1-tap fast access & offline route tracking.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <Button
              onClick={handleInstallClick}
              size="sm"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-xs h-8 px-3.5 gap-1.5 shadow-sm rounded-md border-0 transition-all w-full sm:w-auto"
              data-testid="install-app-banner-btn"
            >
              <Download size={14} />
              Install App
            </Button>
            <button
              onClick={handleDismiss}
              className="p-1 text-slate-400 hover:text-white rounded-md transition-colors"
              aria-label="Close banner"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Guide Dialog for manual installation when prompt isn't triggered directly */}
      <Dialog open={guideModalOpen} onOpenChange={setGuideModalOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-slate-900 font-heading">
              <div className="w-9 h-9 rounded-lg bg-slate-100 p-1 flex items-center justify-center border border-slate-200 shrink-0">
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} className="w-full h-full object-contain" />
                ) : (
                  <OmkarLogo size={28} />
                )}
              </div>
              <span>Install {companyName} App</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-4 text-sm text-slate-700">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
              <div className="font-semibold text-xs uppercase tracking-wider text-cobalt-700 flex items-center gap-1.5">
                <span>📱 Android (Chrome)</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                1. Tap the <strong>3 dots (⋮)</strong> menu in Chrome at the top right.<br />
                2. Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong>.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
              <div className="font-semibold text-xs uppercase tracking-wider text-cobalt-700 flex items-center gap-1.5">
                <span>🍏 iPhone / iPad (Safari)</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                1. Tap the <strong>Share</strong> button at the bottom of Safari.<br />
                2. Scroll down and tap <strong>"Add to Home Screen"</strong>.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
              <div className="font-semibold text-xs uppercase tracking-wider text-cobalt-700 flex items-center gap-1.5">
                <span>💻 Desktop (Chrome / Edge / Brave)</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Click the <strong>Install App icon (⊕ or 💻)</strong> on the far right of your address bar at the top of browser.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setGuideModalOpen(false)} className="bg-cobalt-600 hover:bg-cobalt-700 text-white w-full">
              Got It
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
